<script setup lang="ts">
  import ChatHistoryModal from '@/components/app/chat/sidepanel/ChatHistoryModal.vue'
  import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useToast } from '@/components/ui/toast'
  import { useChatRename } from '@/composables/useChatRename'
  import { useChatSessionHistory } from '@/composables/useChatSessionHistory'
  import { useTranslation } from '@/composables/useTranslation'
  import { injectWithWarn } from '@/composables/injectWithWarn'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { AiChatSessionsService } from '@/types/generated/services.gen'
  import type { AIChatSession } from '@/types/generated/types.gen'
  import { ChevronDown, MessagesSquare } from 'lucide-vue-next'
  import { computed, inject, nextTick, onMounted, ref, watch, type Ref } from 'vue'

  /**
   * Fullscreen AI Assistant chat title control.
   *
   * Renders in the fullscreen KI-Assistent top bar (`/app/ai-assistant/`):
   *
   * - When there is no active session or no messages yet: shows the MessagesSquare icon.
   * - With an active session: shows the session title (click to inline rename) plus a
   *   chevron button that toggles a popover with "My Chats" / "Team Chats" tabs and a
   *   "Show all" entry that opens the full :class:`ChatHistoryModal`.
   */

  const props = defineProps<{
    hasMessages?: boolean
    /**
     * When set (alongside :prop:`moveSessionUuid`), the embedded
     * :class:`ChatHistoryModal` renders in "move to folder" mode for the given
     * session. Used by the fullscreen organize flow so the user can pick a
     * destination folder from the same modal the side-panel chat uses.
     */
    moveSessionTitle?: string | null
    moveSessionUuid?: string | null
  }>()

  const emit = defineEmits<{
    (e: 'moveToFolder', data: { sessionUuid: string; folderUuid: string }): void
    (e: 'moveCancelled'): void
  }>()

  const activeChatSession = injectWithWarn<Ref<AIChatSession | null>>(
    'activeChatSession',
    ref(null),
    'FullscreenAiChatTitle'
  )
  const changeActiveChatSession = injectWithWarn<(chatSession: AIChatSession) => void>(
    'changeActiveChatSession',
    () => {},
    'FullscreenAiChatTitle'
  )

  const globalChatStore = useGlobalChatStore()
  const { toast } = useToast()

  const popoverOpen = ref(false)
  const showAllModalOpen = ref(false)
  const modalInitialTab = ref<'chats' | 'folders' | 'prompts' | 'settings'>('chats')

  /**
   * Shared signal used by :func:`useComposerPrefill` (mounted in
   * :class:`AiChatMessageInputAssistant`) to push a prompt into the textarea.
   * Set this when the user picks a template from the prompts tab of the
   * embedded :class:`ChatHistoryModal` so the fullscreen composer prefills
   * itself the same way the verdict-intro chips do.
   */
  const pendingComposerPrefill = inject<Ref<string | null>>(
    'pendingComposerPrefill',
    ref<string | null>(null)
  )

  function handleUsePrompt(prompt: string) {
    pendingComposerPrefill.value = prompt
  }

  const { t } = useTranslation({
    en: {
      chatHistory: 'Chat history',
      myChats: 'My Chats',
      teamChats: 'Team Chats',
      showAll: 'Show all',
      newChat: 'New Chat',
      renameTitle: 'Rename chat',
      selectFailed: 'Could not open this chat'
    },
    de: {
      chatHistory: 'Chat-Verlauf',
      myChats: 'Meine Chats',
      teamChats: 'Team Chats',
      showAll: 'Alle anzeigen',
      newChat: 'Neuer Chat',
      renameTitle: 'Chat umbenennen',
      selectFailed: 'Chat konnte nicht geöffnet werden'
    },
    fr: {
      chatHistory: 'Historique des chats',
      myChats: 'Mes chats',
      teamChats: "Chats d'équipe",
      showAll: 'Tout afficher',
      newChat: 'Nouveau chat',
      renameTitle: 'Renommer le chat',
      selectFailed: "Impossible d'ouvrir ce chat"
    }
  })

  /**
   * "Active session with messages" — the title/chevron only render once the first
   * message has landed; an empty freshly-created session still shows the icon-only
   * trigger (Figma spec).
   */
  const hasSessionWithMessages = computed(() => !!activeChatSession.value && !!props.hasMessages)

  const activeTab = ref<'my' | 'team'>('my')
  const { mySessions, teamSessions, fetchSessions } = useChatSessionHistory({ popoverOpen })

  /**
   * Force a bypass of the session-list cooldown. Callers (parent dialog flows,
   * inline rename commit) use this so a just-renamed or just-deleted session is
   * reflected in the popover even when the user reopens it within the cooldown.
   */
  const refreshSessions = () => fetchSessions({ force: true })

  const { renameSession: renameChatSession } = useChatRename({ onAfterCommit: refreshSessions })

  /**
   * Open the :class:`ChatHistoryModal` at a specific tab. Exposed so the
   * parent fullscreen shell can drive the organize flow (``folders`` tab with
   * move-mode props) via its ``ChatOptionsMenu``.
   *
   * :param tab: Initial tab to render when the modal opens.
   * :return: None
   */
  function openModalWithTab(tab: 'chats' | 'folders' | 'prompts' | 'settings') {
    modalInitialTab.value = tab
    showAllModalOpen.value = true
  }

  /**
   * Mirror :class:`SidePanelChatHeader`: when the modal closes while we are in
   * move-mode, notify the parent so it can clear the pending move target. Avoids
   * leaving the organize state primed after the user dismisses the modal.
   */
  watch(showAllModalOpen, (isOpen) => {
    if (!isOpen && props.moveSessionTitle) {
      emit('moveCancelled')
    }
  })

  defineExpose({ refreshSessions, openModalWithTab })

  onMounted(() => {
    void fetchSessions()
  })

  function setActiveTab(tab: 'my' | 'team') {
    activeTab.value = tab
  }

  async function handleSelectSession(uuid: string) {
    popoverOpen.value = false
    showAllModalOpen.value = false
    const session =
      mySessions.value.find((s) => s.uuid === uuid) ||
      teamSessions.value.find((s) => s.uuid === uuid) ||
      globalChatStore.chatSessions.find((s) => s.uuid === uuid)
    if (session) {
      changeActiveChatSession(session)
      return
    }
    try {
      const fetched = await AiChatSessionsService.aiChatSessionsRetrieve({ uuid })
      changeActiveChatSession(fetched)
    } catch (error) {
      console.error('Failed to load chat session for selection:', error)
      toast({ title: t('selectFailed'), variant: 'destructive' })
    }
  }

  function handleShowAll() {
    popoverOpen.value = false
    modalInitialTab.value = 'chats'
    showAllModalOpen.value = true
  }

  // --- Inline rename ---
  const isRenaming = ref(false)
  const renameValue = ref('')
  const renameSaving = ref(false)
  const renameInputRef = ref<HTMLInputElement | null>(null)

  const currentTitle = computed(() => activeChatSession.value?.title?.trim() || t('newChat'))

  async function startRenaming() {
    if (!hasSessionWithMessages.value || renameSaving.value) return
    renameValue.value = activeChatSession.value?.title ?? ''
    isRenaming.value = true
    await nextTick()
    renameInputRef.value?.focus()
    renameInputRef.value?.select()
  }

  function cancelRenaming() {
    isRenaming.value = false
    renameValue.value = ''
  }

  async function commitRename() {
    // Guard against double-commit: Enter triggers commitRename via keydown while the
    // input is still focused; when ``isRenaming`` flips to ``false`` the DOM tears
    // down the input and fires ``blur`` → a second commitRename invocation. Bailing
    // when a save is already in flight (or when we've already left rename mode)
    // prevents the late call from stomping the in-progress state.
    if (!isRenaming.value || renameSaving.value) return
    const session = activeChatSession.value
    if (!session?.uuid) {
      cancelRenaming()
      return
    }
    renameSaving.value = true
    try {
      // ``renameChatSession`` handles optimistic update, global-store mirror,
      // rollback and destructive toast internally; it also refreshes the popover
      // on success via the ``onAfterCommit`` hook registered above.
      await renameChatSession(session, renameValue.value)
    } finally {
      renameSaving.value = false
      isRenaming.value = false
      renameValue.value = ''
    }
  }

  function onRenameKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitRename()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelRenaming()
    }
  }

  // Reset rename state when the active session changes.
  watch(
    () => activeChatSession.value?.uuid,
    () => {
      if (isRenaming.value) cancelRenaming()
    }
  )
</script>

<template>
  <Popover v-model:open="popoverOpen">
    <!--
      ``PopoverAnchor`` wraps the whole title row so the popover positions itself
      relative to the row's left edge (== chat name's left edge) instead of the
      chevron ``PopoverTrigger`` on the right. ``align="start"`` on
      ``PopoverContent`` then snaps the popover's left edge to the chat name.
    -->
    <PopoverAnchor as-child>
      <div
        class="flex min-w-0 items-center gap-1"
        data-cy="fullscreen-chat-title"
      >
        <!-- No active session: icon-only trigger opens the popover -->
        <PopoverTrigger
          v-if="!hasSessionWithMessages"
          as-child
        >
          <button
            type="button"
            class="flex size-9 items-center justify-center text-gray-500 hover:opacity-70 dark:text-neutral-400"
            :aria-label="t('chatHistory')"
            data-cy="fullscreen-chat-history-icon"
          >
            <MessagesSquare
              class="size-6 shrink-0"
              :stroke-width="1.65"
            />
          </button>
        </PopoverTrigger>

        <!-- Active session: title (rename on click) + chevron (opens popover) -->
        <template v-else>
          <input
            v-if="isRenaming"
            ref="renameInputRef"
            v-model="renameValue"
            type="text"
            class="min-w-0 max-w-[32ch] truncate rounded border border-purple-300 bg-white px-1.5 py-0.5 text-base
              font-medium leading-6 text-gray-800 outline-none ring-2 ring-purple-200 font-inter
              dark:border-purple-500 dark:bg-neutral-800 dark:text-neutral-200"
            :aria-label="t('renameTitle')"
            :placeholder="t('newChat')"
            :disabled="renameSaving"
            data-cy="fullscreen-chat-title-input"
            @keydown="onRenameKeydown"
            @blur="commitRename"
          />
          <button
            v-else
            type="button"
            class="line-clamp-1 max-w-[32ch] truncate text-left text-base font-medium leading-6 text-gray-800
              font-inter hover:opacity-70 dark:text-neutral-200"
            :aria-label="t('renameTitle')"
            data-cy="fullscreen-chat-title-text"
            @click="startRenaming"
          >
            {{ currentTitle }}
          </button>

          <PopoverTrigger as-child>
            <button
              type="button"
              class="flex size-6 shrink-0 items-center justify-center text-gray-800 hover:opacity-70
                dark:text-neutral-200"
              :aria-label="t('chatHistory')"
              data-cy="fullscreen-chat-history-chevron"
            >
              <ChevronDown
                class="size-6 shrink-0 transition-transform duration-200"
                :class="popoverOpen ? 'rotate-180 text-purple-600' : ''"
                :stroke-width="1.25"
              />
            </button>
          </PopoverTrigger>
        </template>
      </div>
    </PopoverAnchor>

    <PopoverContent
      align="start"
      :side-offset="8"
      class="w-72 overflow-hidden rounded-xl bg-white p-0
        shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)] outline outline-1
        -outline-offset-1 outline-gray-200 dark:bg-neutral-900 dark:outline-neutral-700"
      data-cy="fullscreen-chat-history-popover"
    >
      <div
        class="flex flex-col items-start justify-start gap-2 self-stretch border-b border-gray-50 p-4
          dark:border-neutral-800"
      >
        <div class="inline-flex items-center gap-2.5 self-stretch">
          <div class="inline-flex flex-1 flex-col items-start justify-start gap-5">
            <div
              class="inline-flex items-start justify-start gap-3 self-stretch"
              role="tablist"
              :aria-label="t('chatHistory')"
            >
              <button
                type="button"
                role="tab"
                :aria-selected="activeTab === 'my'"
                class="flex flex-1 shrink-0 items-center justify-center gap-2.5 whitespace-nowrap rounded px-5 py-2.5
                  text-sm font-medium leading-5 text-black font-inter dark:text-neutral-200"
                :class="
                  activeTab === 'my'
                    ? 'outline outline-1 -outline-offset-1 outline-gray-200 dark:outline-neutral-700'
                    : 'rounded-[300px]'
                "
                data-cy="fullscreen-chat-history-tab-my"
                @click="setActiveTab('my')"
              >
                {{ t('myChats') }}
              </button>
              <button
                type="button"
                role="tab"
                :aria-selected="activeTab === 'team'"
                class="flex flex-1 shrink-0 items-center justify-center gap-2.5 whitespace-nowrap rounded px-5 py-2.5
                  text-sm font-medium leading-5 text-black font-inter dark:text-neutral-200"
                :class="
                  activeTab === 'team'
                    ? 'outline outline-1 -outline-offset-1 outline-gray-200 dark:outline-neutral-700'
                    : 'rounded-[300px]'
                "
                data-cy="fullscreen-chat-history-tab-team"
                @click="setActiveTab('team')"
              >
                {{ t('teamChats') }}
              </button>
            </div>

            <template v-if="activeTab === 'my'">
              <button
                v-for="session in mySessions"
                :key="session.uuid"
                type="button"
                class="line-clamp-1 self-stretch text-left text-sm font-normal leading-5 text-gray-600 font-inter
                  hover:text-gray-800 dark:text-neutral-400 dark:hover:text-neutral-200"
                :aria-label="session.title || t('newChat')"
                data-cy="fullscreen-chat-history-session"
                @click="handleSelectSession(session.uuid)"
              >
                {{ session.title }}
              </button>
            </template>
            <template v-else>
              <button
                v-for="session in teamSessions"
                :key="session.uuid"
                type="button"
                class="line-clamp-1 self-stretch text-left text-sm font-normal leading-5 text-gray-600 font-inter
                  hover:text-gray-800 dark:text-neutral-400 dark:hover:text-neutral-200"
                :aria-label="session.title || t('newChat')"
                data-cy="fullscreen-chat-history-session"
                @click="handleSelectSession(session.uuid)"
              >
                {{ session.title }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <div class="flex flex-col items-start justify-center gap-4 self-stretch p-4">
        <div class="flex flex-col items-center justify-center gap-4 self-stretch">
          <button
            type="button"
            class="inline-flex items-center gap-2.5 self-stretch text-left hover:opacity-70"
            :aria-label="t('showAll')"
            data-cy="fullscreen-chat-history-show-all"
            @click="handleShowAll"
          >
            <div class="relative h-5 w-5 overflow-hidden">
              <MessagesSquare
                class="h-5 w-5 text-gray-800 dark:text-neutral-200"
                :stroke-width="1.25"
              />
            </div>
            <div
              class="text-sm font-normal leading-5 text-gray-800 font-inter dark:text-neutral-200"
            >
              {{ t('showAll') }}
            </div>
          </button>
        </div>
      </div>
    </PopoverContent>
  </Popover>

  <ChatHistoryModal
    v-model:open="showAllModalOpen"
    :initial-tab="modalInitialTab"
    :move-session-title="moveSessionTitle"
    :move-session-uuid="moveSessionUuid"
    @select-session="handleSelectSession"
    @use-prompt="handleUsePrompt"
    @move-to-folder="emit('moveToFolder', $event)"
    @sessions-changed="refreshSessions"
  />
</template>
